'use client'

/**
 * LivePhoneMockup — simulazione del canvas mobile del visitatore.
 * Proporzioni realistiche di uno smartphone moderno (~9:19.5).
 * Sfondo identico al LiveViewer: #0a0a14.
 */

import { useAllLiveMatches, useSortedMatchIds } from '@/hooks/useLiveMatch'
import MatchCarousel from './MatchCarousel'
import { TeamId } from '@/types/match'

// Canvas logico (px) — proporzione ~9:19.5, come iPhone 14 Pro
const CANVAS_W = 320
const CANVAS_H = 700

// Sfondo identico al LiveViewer
const APP_BG = '#0a0a14'

export default function LivePhoneMockup({ scale = 0.62 }: { scale?: number }) {
  const { states, connected } = useAllLiveMatches()
  const allIds = useSortedMatchIds(states)

  // Dimensioni reali del wrapper
  const shellW = CANVAS_W * scale + 20
  const shellH = CANVAS_H * scale + 28

  return (
    <div className="relative flex-shrink-0" style={{ width: shellW, height: shellH }}>

      {/* ── Phone shell ────────────────────────────────────────────────────────── */}
      <div
        className="absolute inset-0 rounded-[38px] border-[2.5px] border-white/12"
        style={{
          background: APP_BG,
          boxShadow: [
            '0 0 0 1px rgba(255,255,255,0.04)',
            'inset 0 0 0 1px rgba(255,255,255,0.04)',
            '0 40px 100px rgba(0,0,0,0.7)',
            '0 8px 32px rgba(0,0,0,0.4)',
          ].join(', '),
        }}
      >
        {/* Pulsanti lato sinistro (decorativi) */}
        <div className="absolute -left-[4px] top-[72px]  w-[3px] h-[24px] rounded-l bg-white/8" />
        <div className="absolute -left-[4px] top-[108px] w-[3px] h-[44px] rounded-l bg-white/8" />
        <div className="absolute -left-[4px] top-[162px] w-[3px] h-[44px] rounded-l bg-white/8" />
        {/* Pulsante lato destro */}
        <div className="absolute -right-[4px] top-[110px] w-[3px] h-[64px] rounded-r bg-white/8" />

        {/* Dynamic Island (pill centrale) */}
        <div
          className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center justify-center gap-1.5"
          style={{
            width: 88,
            height: 22,
            borderRadius: 11,
            background: '#000',
          }}
        >
          <div className="w-2 h-2 rounded-full bg-[#0a0a12]" style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)' }} />
          <div className="w-5 h-1.5 rounded-full bg-[#0a0a12]" style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)' }} />
        </div>

        {/* Status bar */}
        <div className="absolute top-[28px] left-0 right-0 px-5 z-10 flex items-center justify-between">
          <span className="text-white/35 font-display font-bold" style={{ fontSize: 8 }}>9:41</span>
          <div className="flex items-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-green-400' : 'bg-white/20'}`} />
            <span className="text-white/25 font-display font-bold uppercase tracking-widest" style={{ fontSize: 7 }}>
              {connected ? 'live' : 'off'}
            </span>
          </div>
        </div>

        {/* ── Screen content — canvas scalato ────────────────────────────────── */}
        <div className="absolute inset-x-[4px] inset-y-[8px] overflow-hidden rounded-[32px] flex justify-center items-center">
          <div
            style={{
              width: CANVAS_W,
              height: CANVAS_H,
              transform: `scale(${scale})`,
              transformOrigin: 'center center',
              flexShrink: 0,
              background: APP_BG,
              position: 'relative',
              overflow: 'hidden',
              borderRadius: `${Math.round(32 / scale)}px`,
              willChange: 'transform',
            }}
          >
            {/* Background photo — usa inset negativo invece di scale per evitare l'overflow con transform */}
            <div
              style={{
                position: 'absolute', inset: -12, pointerEvents: 'none',
                backgroundImage: 'url(/images/Match-week.jpg)',
                backgroundSize: 'cover', backgroundPosition: 'center center',
                filter: 'blur(4px) brightness(0.7)',
              }}
            />
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: APP_BG, opacity: 0.15 }} />

            {/* App layout */}
            <div className="w-full h-full flex flex-col gap-3 px-4 pt-14 pb-5" style={{ position: 'relative', zIndex: 1 }}>

              {/* App header */}
              <div className="flex items-center justify-between mb-0.5">
                <div>
                  <p className="font-display font-bold uppercase tracking-widest text-[#FE7200]" style={{ fontSize: 10 }}>
                    Dinamo Molfetta
                  </p>
                  <h2 className="font-display font-bold uppercase text-white leading-none" style={{ fontSize: 24 }}>
                    Live
                  </h2>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${connected ? 'bg-green-400 animate-pulse' : 'bg-white/20'}`} />
                  <span className="text-white/35 font-display font-bold uppercase tracking-widest" style={{ fontSize: 8 }}>
                    {connected ? 'Connesso' : 'Offline'}
                  </span>
                </div>
              </div>

              {/* Score panels */}
              <MatchCarousel
                matches={allIds.flatMap((teamId) => {
                  const state = states[teamId]
                  return state && state.visible !== false ? [{ teamId: teamId as TeamId, state }] : []
                })}
                visibleCount={3}
              />

              {/* Home indicator */}
              <div className="mt-auto flex justify-center">
                <div className="w-20 h-[3px] rounded-full bg-white/12" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
